import { useCallback, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button, { ButtonVariant } from 'src/components/atoms/Button';
import { Checkbox } from 'src/components/atoms/Checkbox';
import ControlledCheckbox from 'src/components/atoms/Checkbox/ControlledCheckbox';
import Gap from 'src/components/atoms/Gap';
import { Icon } from 'src/components/atoms/Icon';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Row } from 'src/components/atoms/Row';
import Text, { TextVariant } from 'src/components/atoms/Text';
import { ControlledDropdown } from 'src/components/molecules/Dropdown';
import { PDFDocument } from 'src/components/molecules/PDFViewer/PDFViewer';
import PDFModal from 'src/components/organisms/PDFModal/PDFModal';
import {
  clearIVFormFields,
  eligibleDocuments,
  logBasePayEntered,
  logNriCheckboxToggled,
  MISMATCH_CODES
} from 'src/features/incomeVerification';
import { docTypeItem, useProofDocumentTypes } from 'src/features/proofs/hooks';
import { IncomeVerification } from 'src/ts/interfaces/incomeVerification';
import { useDebouncedCallback } from 'use-debounce';
import { ReasonCodes } from './helper';
import {
  Container,
  MismatchDetailRow,
  MismatchInputWrapper,
  MismatchTileWrapper,
  StatusBadge,
  TileIncludeRow
} from './styles';

type IncomeCalculationTileFormProps = {
  document: IncomeVerification;
  reason_codes: ReasonCodes;
  onSave: (formData: IncomeVerification) => void;
  documentErrors?: number[];
  isIncludedInCalculation?: boolean;
  onToggleInclude?: () => void;
  mismatchNames?: { applicant: string; document: string };
  sourceType?: string;
  entryId?: string;
  incomeVerificationReviewId?: string;
  applicantId?: string;
  companyId?: string;
  propertyId?: string;
};

const IncomeCalculationTileForm = ({
  document,
  reason_codes,
  onSave,
  documentErrors,
  isIncludedInCalculation,
  onToggleInclude,
  mismatchNames,
  sourceType,
  entryId,
  incomeVerificationReviewId,
  applicantId,
  companyId,
  propertyId
}: IncomeCalculationTileFormProps) => {
  const hasLoggedBasePayRef = useRef(false);
  const signalContext = {
    applicant_id: applicantId,
    entry_id: entryId,
    company_id: companyId,
    property_id: propertyId,
    document_id: document.id,
    income_verification_review_id: incomeVerificationReviewId
  };
  const methods = useForm<IncomeVerification>({ defaultValues: document, mode: 'onBlur' });
  const { proofDocumentTypes } = useProofDocumentTypes();

  const hasMismatch = documentErrors?.some((code) => MISMATCH_CODES.includes(code)) ?? false;
  const severity = hasMismatch ? (documentErrors?.includes(3003) ? 'error' : 'warning') : undefined;
  const isExcluded = hasMismatch && isIncludedInCalculation === false;
  const isConnectedPayroll = sourceType === 'CONNECTED_PAYROLL';

  const handleOnSave = useDebouncedCallback((data: IncomeVerification) => {
    data = { ...data, index: document.index, file: document.file, docId: document.docId };

    if (!hasLoggedBasePayRef.current && data.basePayIncomeAmount) {
      logBasePayEntered(data.basePayIncomeAmount, signalContext);
      hasLoggedBasePayRef.current = true;
    }

    const payPeriodDateRange = data.payPeriodDateRange;
    if (!payPeriodDateRange) {
      data = {
        ...data,
        payPeriodStartDate: '',
        payPeriodEndDate: ''
      };

      onSave(data);
      return;
    }

    const payPeriodDateRangeSplit = data.payPeriodDateRange.split(' - ');

    if (payPeriodDateRangeSplit.length === 2) {
      const payPeriodStartDate = payPeriodDateRangeSplit[0];
      const payPeriodEndDate = payPeriodDateRangeSplit[1];
      data = {
        ...data,
        payPeriodStartDate,
        payPeriodEndDate
      };
    }

    onSave(data);
  }, 2000);

  const onDocumentTypeChange = useCallback(
    ({
      type: {
        selected: { value: selectedDocumentType }
      }
    }: {
      type: { selected: docTypeItem };
    }) => {
      if (!eligibleDocuments.includes(selectedDocumentType)) {
        clearIVFormFields(methods.setValue);
      }
      const values = methods.getValues();
      handleOnSave(values);
    },
    [handleOnSave, methods.setValue, methods.getValues]
  );

  const [showModal, setShowModal] = useState(false);
  const pdfDocument: PDFDocument = {
    key: document.docId,
    id: document.id,
    file: document.file,
    fileName: document.fileName
  };

  const documentType = methods.watch('type');
  const isDisabledForm =
    isExcluded || isConnectedPayroll || !documentType || !eligibleDocuments.includes(documentType);

  const getBadgeVariant = () => {
    if (isIncludedInCalculation) return 'included-override';
    return severity === 'error' ? 'excluded-error' : 'excluded-warning';
  };

  const tileContent = (
    <Container>
      <Row gap={1} justify="space-between" alignItems="center">
        <Row.Col>
          <Text isBold={true}>Doc ID: {document?.docId} </Text>
        </Row.Col>
        {document?.index >= 0 && <Row.Col> (Doc {document.index + 1})</Row.Col>}
        {hasMismatch && (
          <Row.Col>
            <StatusBadge
              data-testid={`mismatch-badge-${document.docId}`}
              variant={getBadgeVariant()}
            >
              {isIncludedInCalculation ? 'Included' : 'Excluded'}
            </StatusBadge>
          </Row.Col>
        )}
        {document.isAutomated && (
          <Row.Col>
            <StatusBadge variant="automated">Automated VOI</StatusBadge>
          </Row.Col>
        )}
        <Row.Col>
          {isConnectedPayroll ? (
            <StatusBadge variant="connected">Connected Payroll</StatusBadge>
          ) : (
            <Button
              name="view-income-verification-pdf"
              variant={ButtonVariant.outline}
              onClick={() => setShowModal(true)}
            >
              View PDF
            </Button>
          )}
        </Row.Col>
      </Row>
      {hasMismatch && severity && mismatchNames && (
        <MismatchDetailRow
          severity={severity}
          role={severity === 'error' ? 'alert' : 'status'}
          data-testid={`mismatch-banner-${document.docId}`}
        >
          <Icon color={severity === 'error' ? 'red' : 'yellow'} icon="person" />
          <div>
            Name mismatch
            <br />
            <strong>
              &quot;{mismatchNames.applicant}&quot; vs &quot;{mismatchNames.document}&quot;
            </strong>
          </div>
        </MismatchDetailRow>
      )}
      <Gap height={0.5} />
      <div className="tile-form-fields">
        <FormProvider {...methods}>
          <form onChange={methods.handleSubmit(handleOnSave)}>
            <div>
              <Text variant={TextVariant.small}>Document Type</Text>
              <Gap height={0.25} />
              <ControlledDropdown
                name="type"
                data={proofDocumentTypes}
                showLabel={false}
                onChange={onDocumentTypeChange}
                isDisabled={isConnectedPayroll}
              />
            </div>
            <div>
              <Text variant={TextVariant.small}>Pay Period</Text>
              <Gap height={0.25} />
              <InputText
                name="payPeriodDateRange"
                type={InputTextType.text}
                placeholder=""
                showOptionalLabel={false}
                isDisabled={isDisabledForm}
              />
            </div>
            <div>
              <Text variant={TextVariant.small}>Recipient Name</Text>
              <Gap height={0.25} />
              {hasMismatch && severity ? (
                <MismatchInputWrapper severity={severity}>
                  <InputText
                    name="recipientName"
                    type={InputTextType.text}
                    placeholder=""
                    showOptionalLabel={false}
                    isDisabled={isDisabledForm}
                  />
                </MismatchInputWrapper>
              ) : (
                <InputText
                  name="recipientName"
                  type={InputTextType.text}
                  placeholder=""
                  showOptionalLabel={false}
                  isDisabled={isDisabledForm}
                />
              )}
            </div>
            <div>
              <Text variant={TextVariant.small}>Income Source</Text>
              <Gap height={0.25} />
              <InputText
                name="incomeSource"
                type={InputTextType.text}
                placeholder=""
                showOptionalLabel={false}
                isDisabled={isDisabledForm}
              />
            </div>
            <Row gap={1}>
              <Row.Col size={1}>
                <Text variant={TextVariant.small}>Base Pay</Text>
                <Gap height={0.25} />
                <InputText
                  name="basePayIncomeAmount"
                  data-testid={`base-pay-income-amount-${document.docId}`}
                  type={InputTextType.text}
                  placeholder=""
                  showOptionalLabel={false}
                  isDisabled={isDisabledForm}
                />
              </Row.Col>
              <Row.Col size={1}>
                <Text variant={TextVariant.small}>Gross Pay</Text>
                <Gap height={0.25} />
                <InputText
                  name="grossPay"
                  type={InputTextType.text}
                  placeholder=""
                  showOptionalLabel={false}
                  isDisabled={isDisabledForm}
                />
              </Row.Col>
            </Row>
            <ControlledCheckbox
              name="hasNonRecurringIncome"
              data-testid={`nri-checkbox-${document.docId}`}
              label="Non-Recurring Income Present"
              onClick={(data) => {
                logNriCheckboxToggled(data.isChecked, signalContext);
                handleOnSave({ ...methods.getValues(), hasNonRecurringIncome: data.isChecked });
              }}
              showLabel={true}
              isDisabled={isDisabledForm}
            />
          </form>
        </FormProvider>
      </div>
      {hasMismatch && onToggleInclude && (
        <TileIncludeRow data-testid={`include-row-${document.docId}`}>
          <Checkbox
            name={`include-${document.docId}`}
            value={document.docId}
            label="Include in calculation"
            isChecked={isIncludedInCalculation ?? false}
            onClick={() => onToggleInclude()}
            showLabel={true}
          />
        </TileIncludeRow>
      )}
    </Container>
  );

  if (hasMismatch && severity) {
    return (
      <>
        <PDFModal
          document={pdfDocument}
          showModal={showModal}
          setShowModal={setShowModal}
        ></PDFModal>
        <MismatchTileWrapper severity={severity} isExcluded={isExcluded}>
          {tileContent}
        </MismatchTileWrapper>
      </>
    );
  }

  return (
    <>
      <PDFModal document={pdfDocument} showModal={showModal} setShowModal={setShowModal}></PDFModal>
      {tileContent}
    </>
  );
};

export default IncomeCalculationTileForm;
